import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { t as translate } from "./i18n";

const LangContext = createContext({ lang: "it", setLang: () => {}, t: (k) => k });

const PERSIST_KEY = "sei.lang";

/**
 * Lang resolution priority on first paint:
 *  1. `?lang=en|it` query string  (lets Google serve dedicated URLs for hreflang)
 *  2. localStorage  (user's previous choice)
 *  3. browser `navigator.language`  (en-* speakers get EN automatically)
 *  4. "it"  (fallback)
 *
 * When the user toggles lang in-app we update the URL search param (without
 * reloading the SPA) so the canonical URL matches the displayed language.
 * Crawlers visiting `https://dealrating.it/?lang=en` get an EN-rendered HTML
 * and the right hreflang alternates.
 */
const resolveInitialLang = () => {
  if (typeof window === "undefined") return "it";
  const qs = new URLSearchParams(window.location.search).get("lang");
  if (qs === "en" || qs === "it") return qs;
  const stored = localStorage.getItem(PERSIST_KEY);
  if (stored === "en" || stored === "it") return stored;
  const nav = (navigator.language || "").toLowerCase();
  if (nav.startsWith("en")) return "en";
  return "it";
};

export const LangProvider = ({ children }) => {
  const [lang, setLang] = useState(resolveInitialLang);

  // Sync ?lang= in the URL on first mount so hreflang and canonical URLs
  // stay consistent (especially on direct EN landings where the URL still
  // lacks the parameter).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    const current = url.searchParams.get("lang");
    if (current !== lang) {
      url.searchParams.set("lang", lang);
      window.history.replaceState({}, "", url.toString());
    }
    document.documentElement.lang = lang;
  }, [lang]);

  const setAndPersist = useCallback((l) => {
    setLang(l);
    localStorage.setItem(PERSIST_KEY, l);
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.set("lang", l);
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  const t = useCallback((path) => translate(lang, path), [lang]);
  return (
    <LangContext.Provider value={{ lang, setLang: setAndPersist, t }}>
      {children}
    </LangContext.Provider>
  );
};

export const useLang = () => useContext(LangContext);
